import { Repeat } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatAmount } from '@/lib/utils'
import { DEFAULT_CATEGORY_COLOR } from '@/lib/constants'
import type { FrequentMerchant } from '@/lib/statistics'

type Props = {
  merchants: FrequentMerchant[]
  /** Total monthly spend, used to compute share-of-month per merchant. */
  monthlyTotal: number
}

export function FrequentMerchantsCard({ merchants, monthlyTotal }: Props) {
  if (merchants.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <Repeat className="h-4 w-4 text-muted-foreground" />
            よく行く店
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            同じ店への複数回の支出はありません。
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5 text-sm">
          <Repeat className="h-4 w-4 text-muted-foreground" />
          よく行く店
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          同じ店への小さな支出は積み重なりがち
        </p>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2.5">
          {merchants.map((m) => {
            const share = monthlyTotal > 0 ? (m.totalAmount / monthlyTotal) * 100 : 0
            const color = m.parentCategoryColor ?? DEFAULT_CATEGORY_COLOR
            return (
              <li key={m.storeName} className="flex items-center gap-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums text-white"
                  style={{ backgroundColor: color }}
                  aria-hidden
                >
                  {m.visitCount}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{m.storeName}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {m.parentCategoryName ? `${m.parentCategoryName} · ` : ''}
                    1回あたり {formatAmount(m.avgPerVisit)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-sm font-medium tabular-nums">
                    {formatAmount(m.totalAmount)}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {share >= 1 ? `${Math.round(share)}%` : '<1%'}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
